import { useEffect, useState } from "react";
import shortid from "shortid";
import {
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Grid,
  TextField,
  CurrencyInput,
  Paper,
  DataGrid,
  Loading,
} from "../../../../components";
import { formatPrice, responseErros, unformatPrice } from "../../../../utils";
import {
  insumoListValidator,
  insumoValidator,
} from "../middlewares/insumo-servico.validator";
import { InputErros } from "../../../../helpers";
import { InsumosService, notification } from "../../../../services";

export function InsumoServicoDialog({ open, setOpen }) {
  const [insumos, setInsumos] = useState([]);
  const [insumo, setInsumo] = useState({});
  const [isItemEdit, setIsItemEdit] = useState(false);
  const [idsRemoved, setIdsRemoved] = useState([]);
  const [loading, setLoading] = useState(false);
  const insumoService = new InsumosService();

  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    if (open) {
      buscarInsumos();
    }
  }, [open]);

  async function buscarInsumos() {
    setLoading(true);
    const result = await insumoService.getAll();
    if (!result.isAxiosError) {
      setInsumos(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const columns = [
    { field: "descricao", headerName: "Descrição", flex: 300 },
    {
      field: "quantidade",
      headerName: "Quantidade",
      flex: 80,
    },
    {
      field: "valorUnitario",
      headerName: "Valor Unitário",
      flex: 80,
      valueFormatter: (params) => `R$ ${formatPrice(params.value || 0, false)}`,
    },
    {
      field: "valorTotal",
      headerName: "Valor Total",
      flex: 80,
      valueFormatter: (params) => `R$ ${formatPrice(params.value || 0, false)}`,
    },
  ];

  function onClose() {
    setInsumo({});
    setInsumos([]);
    setIdsRemoved([]);
    setIsItemEdit(false);
    setOpen(false);
  }

  function adicionar(item = insumo) {
    const insumoTemp = {
      id: item?.itemEditado ? item.id : shortid.generate(),
      descricao: item.descricao,
      quantidade: parseFloat(item.quantidade) || 0,
      valorUnitario: parseFloat(item.valorUnitario) || 0,
      valorTotal: parseFloat(item.valorTotal) || 0,
    };

    insumoValidator
      .validate(insumoTemp, { abortEarly: false })
      .then(() => {
        if (item?.itemEditado) {
          setInsumos((prev) =>
            prev.map((m) => (m.id === item.id ? insumoTemp : m)),
          );
          setIsItemEdit(true);
        } else {
          setInsumos((prev) => [insumoTemp, ...prev]);
        }
        setInsumo({});
        inputErros.limpar();
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  async function upsertInsumos() {
    insumoListValidator
      .validate(insumos, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const body = { items: insumos, idsRemoved };
        const result = await insumoService.upsert(body);
        if (!result.isAxiosError) {
          notification.alteracaoSucesso();
          onClose();
        } else {
          responseErros(result);
        }
        setLoading(false);
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  function onClickEditInsumo(id) {
    const edit = insumos.find((m) => m.id === id);
    if (edit) {
      setInsumo({ ...edit, itemEditado: true });
    }
  }

  function onClickDeleteInsumo(id) {
    const removed = insumos.find((m) => m.id === id);
    if (removed && typeof removed.id === "number") {
      setIdsRemoved((prev) => [...prev, removed.id]);
    }
    setInsumos((prev) => prev.filter((m) => m.id !== id));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperComponent="div"
      PaperProps={{ style: { borderRadius: "0.7rem", background: "#ffffff" } }}
    >
      <DialogContent>
        <CardHeader title="Novo Insumo" sx={{ padding: "1rem 1.6rem" }} />

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                id="descricao"
                name="descricao"
                label="Descrição"
                required
                value={insumo.descricao || ""}
                onChange={(e) =>
                  setInsumo({ ...insumo, descricao: e.target.value })
                }
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Quantidade"
                type="number"
                value={insumo.quantidade || ""}
                onChange={(e) => {
                  const quantidade = e.target.value;
                  const valorTotal =
                    (parseFloat(quantidade) || 0) *
                    (parseFloat(insumo.valorUnitario) || 0);
                  setInsumo({ ...insumo, quantidade, valorTotal });
                }}
                fullWidth
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Valor Unitário"
                value={insumo.valorUnitario || 0}
                margin="none"
                onChange={(e) =>
                  setInsumo({
                    ...insumo,
                    valorUnitario: unformatPrice(e.target.value),
                    valorTotal:
                      (parseFloat(insumo.quantidade) || 0) *
                      (parseFloat(unformatPrice(e.target.value)) || 0),
                  })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <CurrencyInput
                label="Valor Total"
                value={insumo.valorTotal || 0}
                margin="none"
                onChange={(e) =>
                  setInsumo({
                    ...insumo,
                    valorTotal: unformatPrice(e.target.value),
                  })
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => adicionar(insumo)}
              >
                {insumo.itemEditado ? "Atualizar" : "Adicionar"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
        <Box>
          <DataGrid
            rows={insumos}
            columns={columns}
            onClickEdit={onClickEditInsumo}
            onClickDelete={onClickDeleteInsumo}
            editShow
            deleteShow
          />
        </Box>
      </DialogContent>
      <DialogActions className="me-2">
        <Button
          onClick={onClose}
          color="padrao"
          variant="contained"
          disableElevation
        >
          Fechar
        </Button>
        {((insumos.length > 0 &&
          insumos.some((m) => typeof m.id === "string")) ||
          isItemEdit ||
          idsRemoved?.length > 0) && (
          <Button
            onClick={upsertInsumos}
            color="primary"
            variant="contained"
            disableElevation
          >
            Salvar
          </Button>
        )}
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
}
